import {
  AUTHORIZATION_ROLES,
  AUTHORIZATION_ROLE_LABELS,
  ROLE_CATALOGUE,
  ROLE_DEPARTMENT_LABELS,
  RoleDepartment,
  effectivePermissions,
} from '@scoutaround/script-breakdown-shared';

import { ApiError, type Invitation } from '../../lib/api.js';
import type { WorkState } from '../ui/StatusPill.js';

/**
 * THE ROLE IS THE JOB. Everything here is derived from `ROLE_CATALOGUE`, so
 * adding a role is one entry in `shared/src/authorization.ts` and every picker
 * on the Team page grows on its own.
 */
export const ROLES_BY_DEPARTMENT = RoleDepartment.options
  .map((department) => ({
    department,
    label: ROLE_DEPARTMENT_LABELS[department],
    roles: AUTHORIZATION_ROLES.filter((role) => ROLE_CATALOGUE[role].department === department),
  }))
  .filter((group) => group.roles.length > 0);

/** A job's name. A role this build has never heard of (the column is a varchar
 *  a newer deploy can write) reads as its own word, never as a crash. */
export function roleLabel(role: string): string {
  return (AUTHORIZATION_ROLE_LABELS as Readonly<Record<string, string | undefined>>)[role] ?? role.toLowerCase();
}

/** What the job may change, in one line. */
export function roleMeaning(role: string): string {
  return (ROLE_CATALOGUE as Readonly<Record<string, { summary: string } | undefined>>)[role]?.summary ?? '';
}

/**
 * Whether this production gave the role different permissions from the
 * catalogue — the summary beside a member is the catalogue's, so the list says
 * when that line is no longer the whole story.
 */
export function adjustedFromDefault(role: string, permissions: readonly string[]): boolean {
  const base = effectivePermissions(role);
  if (base.length === 0) return false;
  const held = new Set(permissions);
  return base.length !== held.size || base.some((p) => !held.has(p));
}

/** The grouped role picker, shared by the invite drawer and Change role. */
export function RoleSelect({
  id,
  testId,
  value,
  onChange,
  startHere = false,
}: {
  id: string;
  testId: string;
  value: string;
  onChange: (role: string) => void;
  /** Where focus lands when the dialog holding it opens (`data-autofocus`). */
  startHere?: boolean;
}) {
  return (
    <select
      id={id}
      data-testid={testId}
      data-autofocus={startHere ? true : undefined}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    >
      {ROLES_BY_DEPARTMENT.map((group) => (
        <optgroup key={group.department} label={group.label}>
          {group.roles.map((r) => (
            <option key={r} value={r}>
              {AUTHORIZATION_ROLE_LABELS[r]}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/**
 * WHERE AN INVITATION STANDS, from what the server stores.
 *
 * The list stores three statuses; "expired" is not one of them. The invitation
 * page and the accept call both treat a link past `expiresAt` as expired, so a
 * PENDING row past it is said to be expired here too — it cannot be accepted,
 * and it still blocks a fresh invitation to the same address until it is
 * revoked.
 */
export type InvitationState = 'PENDING' | 'EXPIRED' | 'ACCEPTED' | 'REVOKED' | 'OTHER';

export function invitationState(invitation: Invitation, now: number): InvitationState {
  if (invitation.status === 'PENDING') {
    return new Date(invitation.expiresAt).getTime() < now ? 'EXPIRED' : 'PENDING';
  }
  if (invitation.status === 'ACCEPTED' || invitation.status === 'REVOKED') return invitation.status;
  return 'OTHER';
}

export const INVITATION_PILL: Readonly<Record<Exclude<InvitationState, 'OTHER'>, { state: WorkState; label: string }>> = {
  PENDING: { state: 'IN_PROGRESS', label: 'Pending' },
  EXPIRED: { state: 'OUTDATED', label: 'Expired' },
  ACCEPTED: { state: 'COMPLETED', label: 'Accepted' },
  REVOKED: { state: 'EXCLUDED', label: 'Revoked' },
};

/** A calendar day as the product shows dates: 14.11.2026. */
export function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear())}`;
}

/** The two or three letters on an avatar; `#` while no name can be read. */
export function initialsOf(name: string | null): string {
  if (name === null) return '#';
  const words = name.trim().split(/\s+/).filter((w) => w !== '');
  const letters = words.slice(0, 2).map((w) => w.charAt(0).toUpperCase());
  return letters.join('') || '#';
}

/**
 * Why an invitation was not sent. A coded refusal is an OFFER, not a failure:
 * the producer is being told what to buy, and a generic "could not send" would
 * hide that.
 */
export function inviteRefusal(e: unknown): string {
  if (e instanceof ApiError && e.code === 'FEATURE_NOT_IN_PLAN') {
    return 'Inviting people needs the collaboration module — it is included from Lite upwards. See Plan & billing.';
  }
  if (e instanceof ApiError) return e.message;
  return 'Could not send that invitation.';
}
